import { c as _c } from "react/compiler-runtime";
import React, { useState } from 'react';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { useTranslation } from 'react-i18next';
import { FIELD_BANK_CODE, FIELD_SECOND_PART, FIELD_THIRD_PART } from '@mallpay/orbis';
import { toast } from 'react-toastify';
import { Button } from '@/components/buttons';
import { addPrefixToDataParams } from '@/utils/string';
import { FetcherError, getFetcherErrorItems, getFetcherErrorMessages } from '@/utils/fetcher';
import { usePublicUpsellServicesClaims, type UpsellServiceClaimRequest } from '@/api/skippay/publicUpsellServicesClaims';
import { STEP_SLUGS, stepOrder, type StepSlug, type InsuranceReportFormValues, type SubmissionStatus, type BankAccountValue, FIELD_INSURANCE_NUMBER, FIELD_EVENT_TYPE, FIELD_RECEIPT, EVENT_TYPE_VALUES, FIELD_POLICE_REPORT, FIELD_PHOTOS, FIELD_INCIDENT_DATE, FIELD_INCIDENT_PLACE, FIELD_INCIDENT_DESCRIPTION, FIELD_FULL_NAME, FIELD_DATE_OF_BIRTH, FIELD_EMAIL, FIELD_PHONE, FIELD_BANK_ACCOUNT } from './InsuranceReportForm.types';
import { InsuranceNumberStep } from './steps/InsuranceNumberStep';
import { EventTypeStep } from './steps/EventTypeStep';
import { DocumentsStep } from './steps/DocumentsStep';
import { MainInformationStep } from './steps/MainInformationStep';
import { Success } from './steps/Success';
import { Fail } from '@/sections/insuranceReport/steps/Fail';
import { Link } from '@mallpay/orbis';
import { LINKS } from '@/constants/links';
import { components } from '@/api/skippay/generated/selfCare';
const mapEventTypeToClaimType = (eventType: string | undefined): UpsellServiceClaimRequest['claimType'] => {
  switch (eventType) {
    case EVENT_TYPE_VALUES.THEFT:
      return 'THEFT';
    case EVENT_TYPE_VALUES.DAMAGE:
      return 'DAMAGE';
    case EVENT_TYPE_VALUES.WARRANTY:
      return 'WARRANTY';
    default:
      return 'DAMAGE';
  }
};
const mapDocumentsToApi = (files: components['schemas']['File'][] | undefined): UpsellServiceClaimRequest['receiptDocuments'] => {
  if (!files) return [];
  return files.map(file => ({
    file: {
      filename: file.filename,
      content: file.content,
      contentType: file.contentType
    }
  }));
};
const formatBankAccount = (bankAccount: BankAccountValue | undefined): string => {
  if (!bankAccount) return '';
  const prefix = bankAccount[FIELD_THIRD_PART];
  const number = bankAccount[FIELD_SECOND_PART];
  const code = bankAccount[FIELD_BANK_CODE];
  if (prefix) {
    return `${prefix}-${number}/${code}`;
  }
  return `${number}/${code}`;
};
const API_TO_FORM_FIELD_MAP: Record<string, string> = {
  externalOrderItemId: FIELD_INSURANCE_NUMBER,
  claimType: FIELD_EVENT_TYPE,
  date: FIELD_INCIDENT_DATE,
  place: FIELD_INCIDENT_PLACE,
  description: FIELD_INCIDENT_DESCRIPTION,
  fullName: FIELD_FULL_NAME,
  birthDate: FIELD_DATE_OF_BIRTH,
  email: FIELD_EMAIL,
  phone: FIELD_PHONE,
  bankAccount: FIELD_BANK_ACCOUNT,
  receiptDocuments: FIELD_RECEIPT,
  policeReportDocuments: FIELD_POLICE_REPORT,
  photoDocumentationDocuments: FIELD_PHOTOS
};
const FORM_FIELD_TO_STEP_MAP: Record<string, StepSlug> = {
  [FIELD_INSURANCE_NUMBER]: STEP_SLUGS.INSURANCE_NUMBER,
  [FIELD_EVENT_TYPE]: STEP_SLUGS.EVENT_TYPE,
  [FIELD_RECEIPT]: STEP_SLUGS.DOCUMENTS,
  [FIELD_POLICE_REPORT]: STEP_SLUGS.DOCUMENTS,
  [FIELD_PHOTOS]: STEP_SLUGS.DOCUMENTS,
  [FIELD_INCIDENT_DATE]: STEP_SLUGS.MAIN_INFORMATION,
  [FIELD_INCIDENT_PLACE]: STEP_SLUGS.MAIN_INFORMATION,
  [FIELD_INCIDENT_DESCRIPTION]: STEP_SLUGS.MAIN_INFORMATION,
  [FIELD_FULL_NAME]: STEP_SLUGS.MAIN_INFORMATION,
  [FIELD_DATE_OF_BIRTH]: STEP_SLUGS.MAIN_INFORMATION,
  [FIELD_EMAIL]: STEP_SLUGS.MAIN_INFORMATION,
  [FIELD_PHONE]: STEP_SLUGS.MAIN_INFORMATION,
  [FIELD_BANK_ACCOUNT]: STEP_SLUGS.MAIN_INFORMATION
};
const buttonProps = {
  type: 'button' as const,
  variant: 'primary' as const,
  align: 'center' as const,
  size: 'lg' as const,
  className: 'max-w-[21.5rem] w-full'
};
export const InsuranceReportForm = t0 => {
  const $ = _c(24);
  const {
    currentStep,
    setCurrentStep
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["insuranceReport", "core", "overpayRefund"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    mutateAsync: submitClaim
  } = usePublicUpsellServicesClaims();
  const [status, setStatus] = useState("idle");
  let t2;
  if ($[1] !== setCurrentStep) {
    t2 = slug => {
      setCurrentStep(slug);
    };
    $[1] = setCurrentStep;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const setStep = t2;
  let t3;
  if ($[3] !== currentStep || $[4] !== setStep) {
    t3 = () => {
      const index = stepOrder.indexOf(currentStep);
      const next = stepOrder[index + 1] ?? STEP_SLUGS.RESULT;
      setStep(next);
    };
    $[3] = currentStep;
    $[4] = setStep;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const goToNextStep = t3;
  let t4;
  if ($[6] !== currentStep || $[7] !== setCurrentStep) {
    t4 = () => {
      const index_0 = stepOrder.indexOf(currentStep);
      if (index_0 > 0) {
        setCurrentStep(stepOrder[index_0 - 1]);
      }
    };
    $[6] = currentStep;
    $[7] = setCurrentStep;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const goToPrevStep = t4;
  const isFirstStep = currentStep === STEP_SLUGS.INSURANCE_NUMBER;
  const isLastRealStep = currentStep === STEP_SLUGS.MAIN_INFORMATION;
  let t5;
  if ($[9] !== setStep || $[10] !== submitClaim) {
    t5 = async values => {
      const claimType = mapEventTypeToClaimType(values[FIELD_EVENT_TYPE]);
      const isTheft = claimType === "THEFT";
      const date = values[FIELD_INCIDENT_DATE] || "";
      const place = values[FIELD_INCIDENT_PLACE] || "";
      const description = values[FIELD_INCIDENT_DESCRIPTION] || "";
      const fullName = values[FIELD_FULL_NAME] || "";
      const birthDate = values[FIELD_DATE_OF_BIRTH] || "";
      const email = values[FIELD_EMAIL] || "";
      const phone = values[FIELD_PHONE] || "";
      const conditionalDocuments = isTheft ? {
        policeReportDocuments: mapDocumentsToApi(values[FIELD_POLICE_REPORT])
      } : {
        photoDocumentationDocuments: mapDocumentsToApi(values[FIELD_PHOTOS])
      };
      const request = {
        externalOrderItemId: values[FIELD_INSURANCE_NUMBER],
        claimType,
        date,
        place,
        description,
        fullName,
        birthDate,
        email,
        phone,
        bankAccount: formatBankAccount(values[FIELD_BANK_ACCOUNT]),
        receiptDocuments: mapDocumentsToApi(values[FIELD_RECEIPT]),
        ...conditionalDocuments
      };
      ;
      try {
        await submitClaim(request);
        setStatus("success");
        setStep(STEP_SLUGS.RESULT);
      } catch (t6) {
        const e = t6;
        if (e instanceof FetcherError && e.statusCode === 422) {
          const errors = getFetcherErrorItems(e);
          if (errors && errors.length > 0) {
            const formErrors = {};
            const unmappedErrors = [];
            let firstErrorStep;
            for (const err of errors) {
              const field = err.attribute ? API_TO_FORM_FIELD_MAP[err.attribute] : undefined;
              if (field) {
                if (!(field in formErrors)) {
                  formErrors[field] = err.message;
                }
                const step = FORM_FIELD_TO_STEP_MAP[field];
                if (step && (!firstErrorStep || stepOrder.indexOf(step) < stepOrder.indexOf(firstErrorStep))) {
                  firstErrorStep = step;
                }
              } else {
                unmappedErrors.push(err.message);
              }
            }
            const formErrorMessage = Array.from(new Set(unmappedErrors)).join(" ");
            if (formErrorMessage) {
              toast.error(formErrorMessage);
            }
            if (firstErrorStep) {
              setStep(firstErrorStep);
            }
            return {
              ...formErrors,
              ...(formErrorMessage && {
                [FORM_ERROR]: formErrorMessage
              })
            };
          }
        }
        if (e instanceof FetcherError) {
          const errorMessage = getFetcherErrorMessages(e).join(" ");
          if (errorMessage) {
            toast.error(errorMessage);
            return {
              [FORM_ERROR]: errorMessage
            };
          }
        }
        setStatus("error");
        setStep(STEP_SLUGS.RESULT);
      }
    };
    $[9] = setStep;
    $[10] = submitClaim;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== currentStep || $[13] !== goToNextStep || $[14] !== goToPrevStep || $[15] !== isFirstStep || $[16] !== isLastRealStep || $[17] !== setStep || $[18] !== status || $[19] !== t) {
    t6 = t7 => {
      const {
        handleSubmit,
        form,
        submitting,
        values: values_0
      } = t7;
      const state = form.getState();
      const insuranceNumberValue = values_0[FIELD_INSURANCE_NUMBER];
      const isInsuranceNumberValid = !!insuranceNumberValue && !state.errors?.[FIELD_INSURANCE_NUMBER];
      const eventTypeValue = values_0[FIELD_EVENT_TYPE];
      const isEventTypeValid = eventTypeValue && eventTypeValue.length > 0 && !state.errors?.[FIELD_EVENT_TYPE];
      const isTheft_0 = values_0[FIELD_EVENT_TYPE] === EVENT_TYPE_VALUES.THEFT;
      const receiptValue = values_0[FIELD_RECEIPT];
      const policeReportValue = values_0[FIELD_POLICE_REPORT];
      const photosValue = values_0[FIELD_PHOTOS];
      const isReceiptValid = Array.isArray(receiptValue) && receiptValue.length > 0 && !state.errors?.[FIELD_RECEIPT];
      const isPoliceReportValid = Array.isArray(policeReportValue) && policeReportValue.length > 0 && !state.errors?.[FIELD_POLICE_REPORT];
      const isPhotosValid = Array.isArray(photosValue) && photosValue.length > 0 && !state.errors?.[FIELD_PHOTOS];
      const isDocumentsValid = isReceiptValid && isTheft_0 ? isPoliceReportValid : isPhotosValid;
      const isMainInformationValid = !state.hasValidationErrors;
      if (currentStep === STEP_SLUGS.RESULT) {
        window.scrollTo(0, 0);
        if (status === "success") {
          return <Success />;
        }
        if (status === "error") {
          return <Fail onRetry={() => {
            setStatus("idle");
            setStep(STEP_SLUGS.INSURANCE_NUMBER);
          }} />;
        }
        setStep(STEP_SLUGS.INSURANCE_NUMBER);
        return null;
      }
      return <form method="post" onSubmit={handleSubmit} {...addPrefixToDataParams({
        analyticsParam: "insuranceReportForm",
        testId: "insuranceReportForm",
        prefix: "form"
      })}>{currentStep === STEP_SLUGS.INSURANCE_NUMBER && <InsuranceNumberStep />}{currentStep === STEP_SLUGS.EVENT_TYPE && <EventTypeStep />}{currentStep === STEP_SLUGS.DOCUMENTS && <DocumentsStep isTheft={isTheft_0} />}{currentStep === STEP_SLUGS.MAIN_INFORMATION && <MainInformationStep />}<div className="mt-10 flex flex-col-reverse gap-4 sm:flex-row sm:justify-between">{!isFirstStep && <Button {...buttonProps} variant="secondary" onClick={goToPrevStep} analyticsParam="insuranceReportBack" testId="insuranceReportBack">{t("insuranceReport:form.actions.back")}</Button>}{currentStep === STEP_SLUGS.INSURANCE_NUMBER && <div className="flex w-full flex-col"><Button {...buttonProps} onClick={goToNextStep} disabled={!isInsuranceNumberValid} analyticsParam="insuranceNumberSubmit" testId="insuranceNumberSubmit">{t("insuranceReport:form.actions.next")}</Button><Link href={`${LINKS().conditions}#insurance`} analyticsParam="insuranceReportTerms" testId="insuranceReportTerms" className="mt-4 text-center">{t("insuranceReport:form.terms")}</Link></div>}{currentStep === STEP_SLUGS.EVENT_TYPE && <Button {...buttonProps} onClick={goToNextStep} disabled={!isEventTypeValid} analyticsParam="eventTypeSubmit" testId="eventTypeSubmit">{t("insuranceReport:form.actions.next")}</Button>}{currentStep === STEP_SLUGS.DOCUMENTS && <Button {...buttonProps} onClick={goToNextStep} disabled={!isDocumentsValid} analyticsParam="documentsSubmit" testId="documentsSubmit">{t("insuranceReport:form.actions.next")}</Button>}{isLastRealStep && <Button type="submit" variant="primary" align="center" size="lg" disabled={submitting || !isMainInformationValid} className="w-full max-w-[21.5rem]" analyticsParam="mainInformationSubmit" testId="mainInformationSubmit">{t("insuranceReport:form.actions.next")}</Button>}</div></form>;
    };
    $[12] = currentStep;
    $[13] = goToNextStep;
    $[14] = goToPrevStep;
    $[15] = isFirstStep;
    $[16] = isLastRealStep;
    $[17] = setStep;
    $[18] = status;
    $[19] = t;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t5 || $[22] !== t6) {
    t7 = <Form onSubmit={t5} render={t6} />;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
};